import React from 'react';
import cx from 'classnames';
import styles from './Announcements.module.scss';

import {Link} from 'react-router-dom';
import {Button, Card} from 'reactstrap';
import {isEmpty, last, isEqual, get} from 'lodash';
import {useMembership} from 'contexts/MembershipContext';
import {useAuth} from 'contexts/AuthContext';
import {SUBSCRIPTION_TYPE_LEVEL} from 'configs/membership';
import {buildUrlParams} from 'utils/string';
import {ENABLE_ANALYTICS, ENABLE_ISO_SURVEY} from 'configs/enable-features';
import MuiIcon from 'components/Icons/MuiIcon';

const MIenabled = ENABLE_ANALYTICS && ENABLE_ISO_SURVEY;

function AnnouncementsContent(props) {
  const {contents, category} = props;

  const CATEGORIES_LABEL = {
    upcoming: 'Upcoming Features',
    updates: 'Product Updates',
  };

  const title = CATEGORIES_LABEL[category];

  return (
    <>
      <Card className={styles['announcements--contents--card']}>
        <div
          className={cx(
            styles['announcements--contents--card--header'],
            styles[`announcements--contents--card--header--${category}`]
          )}
        >
          <span>{title}</span>
        </div>
        <div className={cx(styles['announcements--contents--card--body'])}>
          {contents.map((item, index) => {
            const {title, description, action} = item;

            return (
              <React.Fragment key={index}>
                <div
                  key={index}
                  className={
                    styles['announcements--contents--card--body--content']
                  }
                >
                  <strong>{title}</strong>
                  <div>{description}</div>
                  <div>{action}</div>
                </div>
                {last(contents) !== item && (
                  <div>
                    <hr className="p-0 m-1 mx-2" />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Card>
    </>
  );
}

function MIAnnouncementBtnBuilder(props) {
  const {
    isSubscribedToMI,
    isV1,
    adminOrStaff,
    currentPlanType,
    toggle,
    isManualMethod,
    isProcessingPayment,
  } = props;

  switch (isSubscribedToMI) {
    case true:
      return (
        <Button
          tag={Link}
          to="/analytics/certifications"
          color="primary"
          onClick={toggle}
        >
          Explore Now
        </Button>
      );
    default:
      if (isProcessingPayment) {
        return null;
      }

      if (isV1 || isManualMethod) {
        return !adminOrStaff ? (
          <Button tag={Link} to="/contact" color="primary" onClick={toggle}>
            Contact Support
          </Button>
        ) : null;
      } else {
        return !adminOrStaff && !isManualMethod ? (
          <Button
            tag={Link}
            to={buildUrlParams('/membership/plans/checkout', {
              plan:
                currentPlanType === 'free' ? 'pay_as_you_go' : currentPlanType,
              market_intelligence: true, //temporary redirect to membership
              redirect: '/analytics/certifications',
              source: 'product-updates-announcement',
            })}
            color="primary"
            onClick={toggle}
          >
            Get Instant Access
          </Button>
        ) : null;
      }
  }
}

function Announcements(props) {
  const {toggle, isOpen} = props;

  const {
    subscriptionLevel,
    isLegacyUser,
    billingMethod,
    isSubscribedToMI,
    isProcessingPayment,
  } = useMembership();

  const isManualMethod = billingMethod === 'manual';

  const {account} = useAuth();

  const isEnterprise = isEqual(
    subscriptionLevel,
    SUBSCRIPTION_TYPE_LEVEL.enterprise
  );
  const isFree = isEqual(subscriptionLevel, SUBSCRIPTION_TYPE_LEVEL.free);
  const isV1 = isLegacyUser && !isEnterprise && !isFree;
  const accountRole = get(account, 'account_role');
  const adminOrStaff = accountRole !== 'super_admin';

  const currentPlanType = get(
    account,
    'company.subscription.subscription_plans_type'
  );

  const ANNOUNCEMENTS = {
    updates: [
      MIenabled && {
        title: 'Market Intelligence is here!',
        description: (
          <>
            <p>
              Access real-time analytics, ISO History, and a complete view of
              the market — all in one place.
            </p>
            <ul>
              <li>
                Stay current with real-time insights on market shifts and
                trends.
              </li>
              <li>
                Act with confidence using historical and data-driven analysis.
              </li>
              <li>
                Save time with all your market insights in one streamlined
                platform.
              </li>
            </ul>
            {adminOrStaff && !isSubscribedToMI && (
              <div className="text-danger font-italic">
                <MuiIcon name="warning" /> Contact your SuperAdmin to get access
                to Market Intelligence.
              </div>
            )}
          </>
        ),
        action: MIAnnouncementBtnBuilder({
          isSubscribedToMI,
          isV1,
          adminOrStaff,
          currentPlanType,
          toggle,
          isManualMethod,
          isProcessingPayment,
        }),
      },
      {
        title: 'Enhanced Search is Here!',
        description: (
          <>
            <p>
              We have introduced an enhanced Search feature for Verification
              Users. This update provides more relevant search results by
              suggesting multiple companies or certificates based on the
              keywords you enter.
            </p>

            <p>
              <strong>What's New?</strong>
            </p>

            <ul>
              <li>Direct search by company name or certificate number.</li>
              <li>
                Multiple suggestions through a search result page for matching
                companies or certificates based on your keywords.
              </li>
              <li>Improved filters to help you quickly narrow results.</li>
              <li>
                Streamlined verification with fewer steps and faster access to
                information.
              </li>
            </ul>
          </>
        ),
        action: (
          <a target="_self" rel="noopener noreferrer" href="/advanced-search">
            Try Now
          </a>
        ),
      },
      subscriptionLevel <= SUBSCRIPTION_TYPE_LEVEL.standard && {
        title: 'New Subscription Plans Are Here!',
        description: (
          <>
            <p className="mb-0">
              Our new subscription plans are here, giving you more control,
              value, and flexibility in managing your certification needs.
            </p>
            <ul>
              <li>
                <strong>Flexible Plans</strong>: Choose the right plan for your
                usage from light users to heavy API consumers.
              </li>
              <li>
                <strong>Credit-Based System</strong>: Pay only for what you use,
                with better transparency on record access.
              </li>
              <li>
                <strong>Free Trial Options</strong>: Try premium features with
                no commitment.
              </li>
            </ul>
          </>
        ),
        action: (
          <>
            {subscriptionLevel === 0 ? (
              <Button
                tag={Link}
                to="/membership/plans"
                color="primary"
                onClick={toggle}
              >
                View Plans & Get Started
              </Button>
            ) : (
              <a
                target="_self"
                rel="noopener noreferrer"
                href="/membership/plans"
              >
                Subscribe Now
              </a>
            )}
          </>
        ),
      },
    ].filter((v) => v),
    upcoming: [
      subscriptionLevel === 0 && {
        title: 'Exclusive Discounts, Just for You',
        description: (
          <span>
            Soon, you'll be able to get <strong>discount codes</strong> to
            unlock special offers on paid plans, perfect for users and partners
            looking to save while scaling their access.
          </span>
        ),
        action: (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://support.iafcertsearch.org/verifiers/release-notes/release-announcements/upcoming-updates"
          >
            Learn more
          </a>
        ),
      },
    ].filter((v) => v),
  };

  return (
    <div className={cx(styles['announcements'])}>
      <div className={cx(styles['announcements--header'], 'border-bottom')}>
        <h5>Product Updates & Upcoming Features</h5>
        <button
          type="button"
          className="close"
          aria-label="Close"
          onClick={toggle}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div
        className={cx(styles['announcements--contents'], {
          [styles['announcements--contents--open']]: isOpen,
          [styles['announcements--contents--hide']]: !isOpen,
        })}
      >
        {Object.keys(ANNOUNCEMENTS).map((key) => {
          if (isEmpty(ANNOUNCEMENTS[key])) return null;

          return (
            <React.Fragment key={key}>
              <AnnouncementsContent
                key={key}
                category={key}
                contents={ANNOUNCEMENTS[key]}
              />
              {last(Object.keys(ANNOUNCEMENTS)) !== key &&
                !isEmpty(ANNOUNCEMENTS[last(Object.keys(ANNOUNCEMENTS))]) && (
                  <div>
                    <hr className="p-0 my-2" />
                  </div>
                )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default Announcements;
